import { isValidElement, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import { Snackbar } from '@client/components/globals/Snackbar';
import type { AuthError } from 'firebase/auth';
import type { AlertProps } from '@mui/material';
import type { ErrorResponse } from '@sda/interfaces/reducer/loaders.reducer';

const getSnackbarText = (body: string | ErrorResponse): ReactElement => {
  let text = 'An unexpected error has occurred.';

  if (typeof body === 'string') {
    text = body;
  } else if (body.error) {
    text = body.error.message;
  } else if (body && body.message) {
    text = body.message;
  }

  return (<>{text}</>);
};

export const showSnackbar = (
  body: string | ReactElement | ErrorResponse,
  severity: AlertProps['severity'] = 'success',
  action = null,
  autoHideDuration: number = null,
): void => {
  let text: ReactElement;

  if (isValidElement(body)) {
    text = body;
  } else {
    text = getSnackbarText(body as string);
  }

  Snackbar.show({
    body: text,
    severity,
    action,
    autoHideDuration,
  });
};

export const showMessageSuccessSnackbar = (threadId: string) => showSnackbar(
  <>
    Message sent.
    {' '}

    <MuiLink
      id="sda-showcall-view-message-link"
      component={Link}
      to={`/messages/${threadId}`}
    >
      View message
    </MuiLink>
  </>,
);

export const showLoginErrorSnackbar = (e: AuthError, includeContactMessage = true) => {
  console.warn(e.code);

  let message = 'An unexpected error occurred while logging you in';

  switch (e.code) {
    case 'auth/account-exists-with-different-credential':
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
    case 'auth/invalid-credential':
    case 'auth/requires-recent-login':
      message = 'This email is already in use or the credentials are invalid';
      break;
    case 'auth/invalid-email':
      message = 'The email address is not valid.';
      break;
    case 'auth/weak-password':
      message = 'The password being used is too weak and should be changed';
      break;
    case 'auth/expired-action-code':
      message = 'Your reset password link has expired';
      break;
    case 'auth/invalid-action-code':
      message = 'Your reset password link is invalid or has already been used';
      break;
    case 'auth/user-disabled':
    case 'auth/user-not-found':
      message = 'There was an error processing your request';
      break;
    case 'auth/operation-not-allowed':
    case 'auth/wrong-password':
    case 'auth/invalid-verification-code':
    case 'auth/invalid-verification-id':
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number':
    case 'auth/invalid-user-token':
    case 'auth/user-token-expired':
    case 'auth/null-user':
    case 'auth/tenant-id-mismatch':
      message = 'This account doesn\'t exist or the credentials are incorrect';
      break;
    case 'auth/missing-android-pkg-name':
    case 'auth/missing-continue-uri':
    case 'auth/missing-ios-bundle-id':
    case 'auth/invalid-continue-uri':
    case 'auth/unauthorized-continue-uri':
    case 'auth/argument-error':
    case 'auth/invalid-persistence-type':
    case 'auth/unsupported-persistence-type':
    case 'auth/custom-token-mismatch':
    case 'auth/invalid-custom-token':
    case 'auth/quota-exceeded':
      message = `A configuration error has occurred: ${e.code}`;
      break;
    case 'auth/captcha-check-failed':
      message = 'An issue with account verification has occurred';
      break;
    default:
      message = 'An unexpected error occurred while processing your credentials';
      break;
  }

  showSnackbar(
    <>
      {message}
      .

      {Boolean(includeContactMessage) && (
        <>
          {' '}
          Contact

          {' '}

          <MuiLink
            id="sda-loginerror-contact-support-link"
            href={`mailto:${process.env.SUPPORT_EMAIL}`}
          >
            {process.env.SUPPORT_EMAIL}
          </MuiLink>

          {' '}
          for assistance.
        </>
      )}
    </>,
    'error',
  );
};
